import { useCallback, useMemo } from 'react'

import { X } from 'lucide-react' // Using lucide-react for the icon
import type { FC } from 'react'

import { Badge } from '@/components/ui/badge'
import type { Tag } from '@/shared/types'

export interface TagsCellProps {
	currentTags: Tag[]
	availableTags: Record<string, Tag>
	updateTags: (tags: string[]) => void
	disabled?: boolean
}

export const TagsCell: FC<TagsCellProps> = ({
	currentTags,
	availableTags,
	updateTags,
	disabled = true,
}) => {
	const tagsWithColor = useMemo(
		() =>
			currentTags.map(tag => ({
				...tag,
				// Ensure a fallback color if the tag is somehow not in the available list
				bgColor: availableTags[tag._id]?.color || '#cccccc',
			})),
		[currentTags, availableTags]
	)

	const handleDelete = useCallback(
		(tagToDelet: Tag) => {
			const updatedTagIds = currentTags
				.filter(t => t._id !== tagToDelet._id)
				.map(tag => tag._id)
			updateTags(updatedTagIds)
		},
		[currentTags, updateTags]
	)

	if (currentTags.length === 0) {
		return null
	}

	return (
		<div
			role="group"
			aria-label="Tags Cell"
			className="flex flex-wrap gap-1"
		>
			{tagsWithColor.map(tag => {
				return (
					<Badge
						key={tag._id}
						role="listitem"
						aria-label={`${tag.name} tag`}
						// Apply dynamic background color via style prop
						style={{ backgroundColor: tag.bgColor }}
						// Use classes for text color, padding, and layout
						className="flex items-center border-transparent text-primary-foreground"
					>
						{tag.name}
						<X
							role="button"
							aria-hidden={false}
							aria-label={`Delete ${tag.name}`}
							aria-disabled={disabled}
							onClick={
								!disabled ? () => handleDelete(tag) : undefined
							}
							className={`ml-1 h-4 w-4 rounded-full hover:bg-black/20 ${
								disabled
									? 'cursor-not-allowed opacity-50'
									: 'cursor-pointer'
							}`}
						/>
					</Badge>
				)
			})}
		</div>
	)
}
